import { useMemo } from "react";
import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useDisableHotkeysOnCellSelection } from "hooks/hotkeys";
import { useTenantTranslation } from "hooks/formatters";
import type { ParsedTable } from "./types";

interface PreviewDataGridProps {
  table: ParsedTable;
  selectedQuantityCol: number | null;
  maxPreviewRows?: number;
}

export const PreviewDataGrid = ({
  table,
  selectedQuantityCol,
  maxPreviewRows = 20,
}: PreviewDataGridProps) => {
  const { t } = useTenantTranslation();

  const { onCellSelectionModelChange, onCellEditStart, onCellEditStop } =
    useDisableHotkeysOnCellSelection();

  const columns = useMemo((): GridColDef[] => {
    return Array.from({ length: table.numColumns }).map((_, index) => {
      let headerName: string;

      if (table.headers?.[index]) {
        headerName = table.headers[index];
      } else if (index === 0) {
        headerName = t("material");
      } else {
        headerName = `${t("column")} ${index + 1}`;
      }

      return {
        field: `c${index}`,
        headerName,
        width: 160,
        editable: false,
      };
    });
  }, [table.numColumns, table.headers, t]);

  const rows = useMemo(() => {
    // Skip header row if present
    const dataStartRow = table.hasHeaderRow ? 1 : 0;
    const dataRows = table.rows.slice(
      dataStartRow,
      dataStartRow + maxPreviewRows
    );

    return dataRows.map((row, index) => {
      const rowData: Record<string, string> & { id: string } = {
        id: `${index}-${row[0] ?? "row"}`,
      };

      for (let columnIndex = 0; columnIndex < table.numColumns; columnIndex++) {
        rowData[`c${columnIndex}`] = row[columnIndex] ?? "";
      }

      return rowData;
    });
  }, [table.rows, table.numColumns, table.hasHeaderRow, maxPreviewRows]);

  const getCellClassName = (params: { field: string }) => {
    return params.field === `c${selectedQuantityCol?.toString() ?? ""}`
      ? "quantity-col-highlight"
      : "";
  };

  return (
    <Box sx={{ mt: 2 }}>
      <HotkeyCompliantDataGrid
        density="compact"
        rows={rows}
        columns={columns}
        getRowId={(row: { id: string }) => row.id}
        cellSelection
        disableRowSelectionOnClick
        onCellSelectionModelChange={onCellSelectionModelChange}
        onCellEditStart={onCellEditStart}
        onCellEditStop={onCellEditStop}
        getCellClassName={getCellClassName}
        sx={{
          overflow: "auto",
          "& .quantity-col-highlight": {
            bgcolor: "action.hover",
          },
        }}
      />
    </Box>
  );
};
